import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  User,
  Users,
  Smartphone,
  GraduationCap,
  ContactRound,
  Pencil,
  PowerOff,
  RotateCcw,
  Bell,
  Phone,
  Mail,
  ExternalLink,
} from 'lucide-react';
import { api } from '../../api/client';
import { formatCPF, formatPhone } from '../../utils/cpfUtils';
import {
  AppStatusBadge,
  getResponsavelAppStatus,
  ResponsavelAcessoAppPanel,
} from './ResponsavelAcessoApp';
import './responsavel-ficha-desktop.css';

interface AlunoVinculo {
  grauParentesco?: string | null;
  principal?: boolean;
  recebeNotificacao?: boolean;
  aluno: {
    id: string;
    nome: string;
    matricula?: string | null;
    fotoUrl?: string | null;
    ativo?: boolean;
    turma?: { id: string; nome: string; serie?: string; turno?: string } | null;
  };
}

export interface ResponsavelDetalhe {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  fotoUrl?: string | null;
  cpf?: string;
  parentesco?: string;
  dataNascimento?: string;
  ativo: boolean;
  usuarioId?: string | null;
  criadoEm: string;
  atualizadoEm?: string;
  alunos?: AlunoVinculo[];
  usuario?: {
    id: string;
    email: string;
    ativo: boolean;
    statusConta?: string | null;
    tokenExpiraEm?: string | null;
    ativadoEm?: string | null;
    criadoEm?: string | null;
    perfil?: string | null;
  } | null;
}

interface Props {
  responsavelId: string;
  escolaId: string;
  canManage: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  canDeactivate: { ok: boolean; reason?: string };
  onInvited?: () => void;
  defaultTab?: TabKey;
  /** Se fornecido, substitui o navigate interno ao clicar em "Ver perfil do aluno". */
  onOpenAluno?: (alunoId: string) => void;
}

const TABS = [
  { key: 'filhos', label: 'Filhos', icon: GraduationCap },
  { key: 'contato', label: 'Contato', icon: ContactRound },
  { key: 'app', label: 'Acesso ao app', icon: Smartphone },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function calcIdade(iso?: string) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth();
    const day = d.getUTCDate();
    const today = new Date();
    let age = today.getFullYear() - year;
    const m = today.getMonth() - month;
    if (m < 0 || (m === 0 && today.getDate() < day)) age--;
    return age;
  } catch (e) {
    // ignore
  }
  return null;
}

function formatarDataNascimento(iso?: string) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (!isNaN(d.getTime())) {
      const day = String(d.getUTCDate()).padStart(2, '0');
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const year = d.getUTCFullYear();
      return `${day}/${month}/${year}`;
    }
  } catch (e) {
    // ignore
  }
  return '—';
}

function hasFoto(fotoUrl?: string | null) {
  if (!fotoUrl) return false;
  const u = fotoUrl.toLowerCase();
  return !u.includes('i.pravatar.cc') && !u.includes('ui-avatars.com');
}

export function ResponsavelFichaDesktop({
  responsavelId,
  escolaId,
  canManage,
  onClose,
  onInvited,
  defaultTab,
  onOpenAluno,
}: Props) {
  const [tab, setTab] = useState<TabKey>(defaultTab || 'filhos');

  useEffect(() => {
    if (defaultTab) {
      setTab(defaultTab);
    }
  }, [defaultTab, responsavelId]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = '/' + (location.pathname.split('/')[1] || 'admin');

  const openAlunoPerfil = (alunoId: string) => {
    onClose();
    navigate(`${basePath}/alunos/${alunoId}`);
  };

  const { data: resp, isLoading } = useQuery({
    queryKey: ['responsavel-ficha', escolaId, responsavelId],
    queryFn: async () => {
      const { data } = await api.get<ResponsavelDetalhe>(
        `/escolas/${escolaId}/responsaveis/${responsavelId}`,
      );
      return data;
    },
    enabled: !!escolaId && !!responsavelId,
  });

  const handleInvited = () => {
    void queryClient.invalidateQueries({ queryKey: ['responsavel-ficha', escolaId, responsavelId] });
    onInvited?.();
  };

  const vinculos = resp?.alunos ?? [];
  const idade = calcIdade(resp?.dataNascimento);
  const telefoneFmt = formatPhone(resp?.telefone || '') || resp?.telefone || '';
  const emailFmt = resp?.email?.trim() || '';

  if (isLoading || !resp) {
    return (
      <div className="resp-ficha">
        <div className="resp-ficha-empty">Carregando ficha…</div>
      </div>
    );
  }

  return (
    <div className="resp-ficha">
      <header className="resp-ficha-head">
        {hasFoto(resp.fotoUrl) ? (
          <img src={resp.fotoUrl!} alt="" className="resp-ficha-avatar" />
        ) : (
          <span className="resp-ficha-avatar-ph">
            <User size={32} />
          </span>
        )}
        <div className="resp-ficha-head-main">
          <p className="resp-ficha-eyebrow">Responsável · ficha da escola</p>
          <h2 className="resp-ficha-name">{resp.nome}</h2>
          <div className="resp-ficha-meta-line">
            <span className="resp-ficha-sub">
              {resp.parentesco || 'Parentesco não informado'}
              {idade != null ? ` · ${idade} anos` : ''}
            </span>
            <span className={`resp-ficha-badge ${resp.ativo ? 'resp-ficha-badge--ok' : 'resp-ficha-badge--off'}`}>
              {resp.ativo ? 'Cadastro ativo' : 'Inativo'}
            </span>
            <AppStatusBadge status={getResponsavelAppStatus(resp)} />
          </div>
          {(telefoneFmt || emailFmt) && (
            <p className="resp-ficha-contact-line">
              {telefoneFmt && (
                <span>
                  <Phone size={11} aria-hidden />
                  {telefoneFmt}
                </span>
              )}
              {telefoneFmt && emailFmt && <span className="resp-ficha-contact-sep" aria-hidden>·</span>}
              {emailFmt && (
                <span>
                  <Mail size={11} aria-hidden />
                  {emailFmt}
                </span>
              )}
            </p>
          )}
        </div>
        <button type="button" className="resp-ficha-close" onClick={onClose} aria-label="Fechar">
          <X size={16} />
        </button>
      </header>

      <div className="resp-ficha-body">
        <div className="resp-ficha-stack">
          <nav className="resp-ficha-tabs" aria-label="Secções da ficha">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  className={`resp-ficha-tab${tab === t.key ? ' resp-ficha-tab--active' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  <Icon size={12} />
                  {t.label}
                  {t.key === 'filhos' && vinculos.length > 0 && (
                    <span className="resp-ficha-tab-count">({vinculos.length})</span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="resp-ficha-panel">
            {tab === 'filhos' && (
              <div className="resp-ficha-card resp-ficha-card--full resp-ficha-card--filhos">
                <div className="resp-ficha-card-body resp-ficha-card-body--filhos">
                  {vinculos.length > 0 ? (
                    <div className="resp-ficha-alunos-grid">
                      {vinculos.map((v) => (
                        <article key={v.aluno.id} className="resp-ficha-aluno-card">
                          <div className="resp-ficha-aluno-card-top">
                            {hasFoto(v.aluno.fotoUrl) ? (
                              <img src={v.aluno.fotoUrl!} alt="" className="resp-ficha-aluno-card-avatar" />
                            ) : (
                              <span className="resp-ficha-aluno-card-avatar-ph">
                                <Users size={22} />
                              </span>
                            )}
                            <div className="resp-ficha-aluno-card-tags">
                              {v.principal && (
                                <span className="resp-ficha-tag resp-ficha-tag--pri">Principal</span>
                              )}
                              {v.recebeNotificacao && (
                                <span className="resp-ficha-tag resp-ficha-tag--notif">
                                  <Bell size={9} style={{ marginRight: 2 }} />
                                  Alertas
                                </span>
                              )}
                              {v.aluno.ativo === false && (
                                <span className="resp-ficha-tag resp-ficha-tag--off">Inativo</span>
                              )}
                            </div>
                          </div>
                          <h4 className="resp-ficha-aluno-card-name">{v.aluno.nome}</h4>
                          <dl className="resp-ficha-aluno-card-dl">
                            <div>
                              <dt>Matrícula</dt>
                              <dd>{v.aluno.matricula || '—'}</dd>
                            </div>
                            <div>
                              <dt>Turma</dt>
                              <dd>{v.aluno.turma?.nome || 'Sem turma'}</dd>
                            </div>
                            <div>
                              <dt>Turno</dt>
                              <dd>{v.aluno.turma?.turno || '—'}</dd>
                            </div>
                            <div>
                              <dt>Parentesco</dt>
                              <dd>{v.grauParentesco || resp.parentesco || '—'}</dd>
                            </div>
                          </dl>
                          <button
                            type="button"
                            className="resp-ficha-aluno-card-btn"
                            onClick={() => {
                              if (onOpenAluno) {
                                onOpenAluno(v.aluno.id);
                              } else {
                                onClose();
                                navigate(`${basePath}/alunos/${v.aluno.id}`);
                              }
                            }}
                          >
                            <ExternalLink size={14} />
                            Ver perfil do aluno
                          </button>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="resp-ficha-empty">
                      <Users size={28} className="resp-ficha-empty-icon" />
                      <p>Nenhum aluno vinculado a este responsável na escola.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'contato' && (
              <div className="resp-ficha-card resp-ficha-card--full resp-ficha-card--contato">
                <dl className="resp-ficha-dl resp-ficha-dl--contato">
                  <div className="resp-ficha-dl-span">
                    <dt>Nome completo</dt>
                    <dd>{resp.nome}</dd>
                  </div>
                  <div>
                    <dt>CPF</dt>
                    <dd>{formatCPF(resp.cpf || '') || '—'}</dd>
                  </div>
                  <div>
                    <dt>Telefone</dt>
                    <dd>{formatPhone(resp.telefone || '') || resp.telefone || '—'}</dd>
                  </div>
                  <div className="resp-ficha-dl-span">
                    <dt>E-mail</dt>
                    <dd>{resp.email || '—'}</dd>
                  </div>
                  <div>
                    <dt>Data de nascimento</dt>
                    <dd>
                      {formatarDataNascimento(resp.dataNascimento)}
                      {idade != null ? ` (${idade} anos)` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt>Parentesco declarado</dt>
                    <dd>{resp.parentesco || '—'}</dd>
                  </div>
                  <div>
                    <dt>Cadastrado em</dt>
                    <dd>
                      {resp.criadoEm
                        ? new Date(resp.criadoEm).toLocaleDateString('pt-BR')
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>Atualizado em</dt>
                    <dd>
                      {resp.atualizadoEm
                        ? new Date(resp.atualizadoEm).toLocaleDateString('pt-BR')
                        : '—'}
                    </dd>
                  </div>
                  {resp.usuario?.ativadoEm && (
                    <div className="resp-ficha-dl-span">
                      <dt>App ativado em</dt>
                      <dd>
                        {new Date(resp.usuario.ativadoEm).toLocaleString('pt-BR')}
                      </dd>
                    </div>
                  )}
                </dl>
                <p className="resp-ficha-contato-note">
                  Endereço e mapa não fazem parte do cadastro do responsável — esses dados
                  ficam na ficha do aluno.
                </p>
              </div>
            )}

            {tab === 'app' && (
              <div className="resp-ficha-card resp-ficha-card--full resp-ficha-card--app">
                <ResponsavelAcessoAppPanel
                  responsavel={resp}
                  escolaId={escolaId}
                  canManage={canManage}
                  compact
                  onInvited={handleInvited}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ResponsavelFichaFooter({
  resp,
  canManage,
  onEdit,
  onDeactivate,
  onReactivate,
  canDeactivate,
}: {
  resp: { criadoEm: string; ativo: boolean };
  canManage: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  canDeactivate: { ok: boolean; reason?: string };
}) {
  if (!canManage) {
    return (
      <div className="resp-ficha-foot">
        <span className="resp-ficha-foot-meta">Somente consulta</span>
        <span className="resp-ficha-foot-spacer" />
      </div>
    );
  }

  return (
    <div className="resp-ficha-foot">
      <span className="resp-ficha-foot-spacer" />
      <div className="resp-ficha-foot-actions">
        <button type="button" className="resp-ficha-btn resp-ficha-btn--primary" onClick={onEdit}>
          <Pencil size={14} strokeWidth={2} />
          Editar
        </button>
        {resp.ativo ? (
          <button
            type="button"
            className={`resp-ficha-btn resp-ficha-btn--danger${!canDeactivate.ok ? ' resp-ficha-btn--disabled' : ''}`}
            title={canDeactivate.ok ? 'Desativar' : canDeactivate.reason}
            onClick={onDeactivate}
          >
            <PowerOff size={14} strokeWidth={2} />
            Desativar
          </button>
        ) : (
          <button type="button" className="resp-ficha-btn resp-ficha-btn--success" onClick={onReactivate}>
            <RotateCcw size={14} strokeWidth={2} />
            Reativar
          </button>
        )}
      </div>
    </div>
  );
}
